/**
 * Password resets — the operator half of recovery (ADR-068).
 *
 * Before this screen existed the feature was built end to end and unusable: a
 * player who forgot their password filed a request that landed in a table
 * nobody could see, and there was no path in the product by which anyone could
 * hand them a link. Everything else was finished. This is the missing half.
 *
 * THREE PROPERTIES THIS SCREEN IS SHAPED BY:
 *
 *   1. **The link cannot be re-read.** Only the SHA-256 of a token is stored.
 *      Issuing MINTS A NEW token, returns it once in the HTTP response, and
 *      supersedes the original request. So the dialog that shows it says
 *      plainly that closing it loses it, the value lives in one piece of
 *      component state, and it is never logged, never toasted, never put in a
 *      URL and never handed to `useAsync` (whose result outlives the render).
 *
 *   2. **The operator verifies the person, not the panel.** Nothing here proves
 *      identity — a queue row is a claim by whoever typed a username into the
 *      forgot form, which is the one place in the product a stranger can point
 *      at somebody else's account. So the panel shows what an operator needs to
 *      check somebody out of band (username, masked address, when, from where,
 *      how many open requests) and nothing that would let them BE the player.
 *
 *   3. **The reason is mandatory.** The server refuses a body without one, and
 *      the button here stays disabled until it is typed — a control that
 *      enables and then 400s teaches the operator to click first and read after.
 *
 * Reading is `viewer`; issuing is `approver` and audited as
 * `player.password_reset_issue`. The role check below is presentation — the
 * server's `requireOperator` is the control.
 */
import { useRef, useState, type ReactNode } from 'react';
import { currentOperator } from '../api/auth.ts';
import {
  fetchPendingResets,
  issuePasswordReset,
  type IssuedReset,
  type PendingReset,
} from '../api/recovery.ts';
import { useAsync } from '../lib/useAsync.ts';
import { absolute, age, shortTime } from '../lib/datetime.ts';
import { Badge, Card, EmptyState, ErrorState, TableSkeleton } from '../ui/primitives.tsx';
import { useConfirm, useToast } from '../ui/feedback.tsx';
import { IconAlert, IconLock, IconRefresh, IconShield } from '../ui/icons.tsx';

const COLUMNS = ['text', 'text', 'text', 'text', 'text', 'num'] as const;

/** The server's own minimum for the audited reason. Mirrored so the UI agrees. */
const MIN_REASON = 3;

export function PasswordResets(): ReactNode {
  const queue = useAsync(() => fetchPendingResets(100), []);
  const [selected, setSelected] = useState<number | null>(null);

  const rows = queue.data ?? [];
  const active = rows.find((r) => r.id === selected) ?? null;

  return (
    <div className="split">
      <div className="stack">
        <div className="banner banner--slim banner--warn">
          <span className="banner__icon">
            <IconLock />
          </span>
          <div className="banner__body">
            <span className="banner__title">Verify the person first.</span>
            <span className="banner__text">
              A row here is a claim typed into the forgot-password form by whoever was at the
              keyboard — it is not proof of identity. Issuing hands someone a live way into an
              account, and the link is shown once and never again.
            </span>
          </div>
        </div>

        <div className="toolbar">
          <span className="toolbar__note">
            Unresolved requests, newest first. Expired ones stay listed — the person is still
            locked out.
          </span>
          <span className="card__spacer" />
          <button type="button" className="btn btn--sm btn--ghost" onClick={queue.reload}>
            <IconRefresh size="sm" />
            Refresh
          </button>
        </div>

        <Card title="Recovery requests" icon={<IconLock />} count={rows.length} flush>
          {queue.error && rows.length === 0 ? (
            <ErrorState
              title="The reset queue did not load"
              detail={queue.error}
              onRetry={queue.reload}
            />
          ) : queue.loading && rows.length === 0 ? (
            <TableSkeleton columns={COLUMNS} rows={6} />
          ) : rows.length === 0 ? (
            <EmptyState
              title="Nobody is waiting"
              hint="No player has an unresolved recovery request. A request appears here the moment someone uses the forgot-password form and no mail could be delivered."
            />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Player</th>
                    <th>Address on file</th>
                    <th>Origin</th>
                    <th>Link state</th>
                    <th className="right">Asked</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      className={`row--clickable${row.id === selected ? ' is-selected' : ''}`}
                      onClick={() => setSelected(row.id)}
                    >
                      <td className="mono dim">{row.id}</td>
                      <td className="nowrap cell-lead">
                        {row.username}
                        <span className="cell-sub">#{row.userId}</span>
                      </td>
                      <td className="muted nowrap">{row.emailMasked ?? '— (username only)'}</td>
                      <td className="nowrap">
                        <Badge tone={row.source === 'operator' ? 'accent' : 'neutral'}>
                          {row.source === 'operator' ? 'issued here' : 'player asked'}
                        </Badge>
                      </td>
                      <td className="nowrap">
                        {row.expired ? (
                          <Badge tone="loss">expired</Badge>
                        ) : (
                          <Badge tone="pending">live · expires {shortTime(row.expiresAt)}</Badge>
                        )}
                      </td>
                      <td className="right mono muted" title={absolute(row.createdAt)}>
                        {age(row.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      <IssuePanel
        request={active}
        openForSameUser={active ? rows.filter((r) => r.userId === active.userId).length : 0}
        onIssued={() => {
          setSelected(null);
          queue.reload();
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------- the panel

function IssuePanel({
  request,
  openForSameUser,
  onIssued,
}: {
  request: PendingReset | null;
  openForSameUser: number;
  onIssued: () => void;
}): ReactNode {
  const confirm = useConfirm();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  /**
   * THE LIVE CREDENTIAL. One piece of state, one component, cleared the moment
   * the dialog closes. Nothing else in this panel may hold it.
   */
  const [issued, setIssued] = useState<IssuedReset | null>(null);

  const me = currentOperator();
  const canIssue = me?.role === 'approver' || me?.role === 'admin';
  const reasonReady = reason.trim().length >= MIN_REASON;

  if (!request) {
    return (
      <Card title="Issue a link" icon={<IconShield />}>
        <EmptyState
          compact
          icon={<IconLock size="lg" />}
          title="No request selected"
          hint="Pick a row. The panel shows what you need to verify the person by phone or by an existing support thread — and nothing that would let you sign in as them."
        />
      </Card>
    );
  }

  async function issue(): Promise<void> {
    if (!request || !reasonReady || busy) return;
    const why = reason.trim();

    const result = await confirm({
      title: 'Issue a one-time reset link',
      intent: 'danger',
      body: (
        <>
          This mints a NEW link and cancels the request it replaces. The link is shown to you
          once, on the next screen. It is not stored anywhere and cannot be looked up again —
          if you lose it, you issue another one and this player waits longer.
        </>
      ),
      facts: [
        { label: 'Player', value: `${request.username} #${request.userId}` },
        { label: 'Request', value: `#${request.id} · asked ${age(request.createdAt)} ago` },
        { label: 'Address on file', value: request.emailMasked ?? '— (username only)' },
        { label: 'Reason', value: why },
      ],
      confirmLabel: 'Issue the link',
    });
    if (!result.confirmed) return;

    setBusy(true);
    try {
      const link = await issuePasswordReset(request.id, why);
      // Straight into the dialog's state. Never a toast, never a log line.
      setIssued(link);
      setReason('');
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : 'The link could not be issued.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack">
      <Card title={`Request #${request.id}`} icon={<IconShield />}>
        <div className="kv kv--spaced">
          <span className="kv__k">Player</span>
          <span className="kv__v kv__v--lead">
            {request.username} <span className="dim mono">#{request.userId}</span>
          </span>
          <span className="kv__k">Address on file</span>
          <span className="kv__v">{request.emailMasked ?? '— (username only, nothing to mail)'}</span>
          <span className="kv__k">Asked</span>
          <span className="kv__v" title={absolute(request.createdAt)}>
            {shortTime(request.createdAt)} · {age(request.createdAt)} ago
          </span>
          <span className="kv__k">From address</span>
          <span className="kv__v mono-code">{request.requestedIp ?? '—'}</span>
          <span className="kv__k">Origin</span>
          <span className="kv__v">
            {request.source === 'operator'
              ? 'A link was already issued from this panel'
              : 'The player used the forgot-password form'}
          </span>
          <span className="kv__k">Delivery</span>
          <span className="kv__v">
            {request.delivered
              ? `Sent via ${request.deliveredVia ?? 'mail'}`
              : `Not delivered${request.deliveredVia ? ` (${request.deliveredVia})` : ''} — this queue is the delivery`}
          </span>
          <span className="kv__k">Link state</span>
          <span className="kv__v">
            {request.expired ? (
              <Badge tone="loss">expired {age(request.expiresAt)} ago</Badge>
            ) : (
              <Badge tone="pending">live until {shortTime(request.expiresAt)}</Badge>
            )}
          </span>
          <span className="kv__k">Open requests</span>
          <span className="kv__v">
            {openForSameUser} in this queue for this account
            {openForSameUser > 2 ? (
              <>
                {' '}
                <Badge tone="loss">unusual</Badge>
              </>
            ) : null}
          </span>
        </div>

        <p className="field__hint field__hint--block">
          Verify the person on a channel that already exists — the phone number on the account, an
          open support thread, a deposit they can describe. The queue proves somebody typed a
          username; it proves nothing about who.
        </p>
      </Card>

      <Card title="Issue" icon={<IconLock />}>
        <label className="field">
          <span className="field__label">Reason (required, audited)</span>
          <textarea
            className="textarea"
            rows={3}
            value={reason}
            disabled={!canIssue || busy}
            placeholder="How you verified this person — e.g. called +91••••••01 on file, confirmed last deposit amount and date."
            onChange={(event) => setReason(event.target.value)}
          />
          <span className="field__hint">
            Written to the operator audit log against your account. The link itself never is —
            an append-only table is the last place a live credential should sit.
          </span>
        </label>

        <div className="row-actions row-actions--start">
          <button
            type="button"
            className="btn btn--primary"
            disabled={!canIssue || busy || !reasonReady}
            onClick={() => void issue()}
          >
            {busy ? 'Issuing…' : 'Issue one-time link'}
          </button>
          {!canIssue ? (
            <span className="field__hint">
              Read-only for your role. Issuing a link needs approver — the server refuses it
              regardless of what this panel shows.
            </span>
          ) : !reasonReady ? (
            <span className="field__hint">Type why you are issuing this, and the button enables.</span>
          ) : null}
        </div>
      </Card>

      {issued ? (
        <OneTimeLinkDialog
          issued={issued}
          onClose={() => {
            const username = issued.username;
            setIssued(null);
            toast(`Link issued for ${username}. It is not recoverable — issue a new one if lost.`);
            onIssued();
          }}
        />
      ) : null}
    </div>
  );
}

// ------------------------------------------------------------ the one-time UI

/**
 * The only place in this product where a live credential is on a screen.
 *
 * It is a plain dialog rather than the shared `confirm()` because that one is
 * dismissed by Escape and by cancel, and either of those would silently throw
 * away something unrecoverable. Here the single way out is the button that says
 * so. Nothing about this value is written anywhere except the clipboard, at the
 * operator's explicit click.
 */
function OneTimeLinkDialog({
  issued,
  onClose,
}: {
  issued: IssuedReset;
  onClose: () => void;
}): ReactNode {
  const [copied, setCopied] = useState(false);
  const [manual, setManual] = useState(false);
  const field = useRef<HTMLInputElement>(null);

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(issued.link);
      setCopied(true);
      setManual(false);
    } catch {
      // No clipboard permission, or a non-secure origin. Select it instead so
      // the operator can copy by hand — never a silent failure on the one
      // value that cannot be fetched again.
      field.current?.select();
      setManual(true);
    }
  }

  return (
    <div className="scrim" role="dialog" aria-modal="true" aria-label="One-time reset link">
      <div className="modal">
        <div className="modal__head">
          <span className="modal__icon modal__icon--danger">
            <IconAlert size="lg" />
          </span>
          <h2 className="modal__title">Copy this now — it is shown once</h2>
        </div>

        <div className="modal__body">
          Send it to {issued.username} on the channel you verified them on. Closing this dialog
          discards it: only a hash is stored, so nobody — including us — can read it back. If it
          is lost, issue another link and this player waits longer.
        </div>

        <label className="field">
          <span className="field__label">One-time link</span>
          <input
            ref={field}
            className="input mono-code"
            readOnly
            value={issued.link}
            onFocus={(event) => event.currentTarget.select()}
          />
          <span className="field__hint">
            Expires {shortTime(issued.expiresAt)} ({age(issued.expiresAt)} from now) and works
            exactly once. Opening it ends every other session on the account.
          </span>
        </label>

        <div className="modal__facts">
          <span className="modal__fact-label">Player</span>
          <span className="modal__fact-value">
            {issued.username} #{issued.userId}
          </span>
          <span className="modal__fact-label">New request</span>
          <span className="modal__fact-value">
            #{issued.resetId} · replaces #{issued.supersedes}
          </span>
        </div>

        {manual ? (
          <p className="field__error">
            The clipboard is not available here — the link is selected in the field above, copy it
            by hand before closing.
          </p>
        ) : null}

        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={() => void copy()}>
            {copied ? 'Copied' : 'Copy link'}
          </button>
          <button type="button" className="btn btn--primary" onClick={onClose}>
            I have sent it — close and discard
          </button>
        </div>
      </div>
    </div>
  );
}
